import { useAuth0 } from '@auth0/auth0-react';
import { CollapsibleSidebarAndMainContent } from '@strata/react-collapsible-sidebar-and-main-content';
import { LeftSideNav } from '@strata/react-left-side-navbar';
import { useFlags } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router';

import { UserType } from 'services/constants';
import {
    ListSchema,
    ListSchemaAppRoutePrefix,
    ListTypeNameMap,
    ListTypesForSchema,
} from 'services/ListManagementHelpers';
import { globalRoutesAppRoute } from 'services/RouteRuleHelpers';

export const SideNav = ({ children }) => {
    const { user, logout } = useAuth0();
    const location = useLocation();
    const { showGlobalRoutingConfig, showTherapyGroupListConfig } = useFlags();
    const navTabContent = [
        {
            navSection: 'Payer Management',
            navItems: [
                {
                    label: 'Payer Segments',
                    to: '/payer-segment',
                },
                {
                    label: 'Go-Lives',
                    to: '/go-lives',
                },
                {
                    label: 'Payer Markets',
                    to: '/payer-market',
                },
            ],
        },
        {
            navSection: 'Item Management',
            navItems: [
                {
                    label: 'Diagnoses',
                    to: '/diagnoses',
                },
                {
                    label: 'Product Services',
                    to: '/product-service',
                },
                {
                    label: 'Product Service History',
                    to: '/product-service-history',
                },
            ],
        },
        {
            navSection: 'Value Case Management',
            navItems: [
                {
                    label: 'Value Cases',
                    to: '/value-cases',
                },
            ],
        },
        {
            navSection: 'Product Management',
            navItems: [
                {
                    label: 'View Product Configuration',
                    to: '/product-config-list',
                },
                {
                    label: 'Parent Products',
                    to: '/parent-products',
                },
                {
                    label: 'View Clinical Policy',
                    to: '/policy-list',
                },
                {
                    label: 'Rate Rules',
                    to: '/rate-rules',
                },
                {
                    label: 'Price Lists',
                    to: '/price-list',
                },
                ...(showTherapyGroupListConfig
                    ? [
                          {
                              label: 'Therapy Groups',
                              to: '/therapy-groups',
                          },
                      ]
                    : []),
            ],
        },
        {
            navSection: 'External User Management',
            navItems: [
                {
                    label: 'Supplier Users',
                    to: `/user-management/${UserType.SUPPLIER}`,
                },
                {
                    label: 'Prescriber Users',
                    to: `/user-management/${UserType.PRESCRIBER}`,
                },
                {
                    label: 'Payer Users',
                    to: `/user-management/${UserType.PAYER}`,
                },
            ],
        },
        {
            navSection: 'Internal User Management',
            navItems: [
                {
                    label: 'Compass Users',
                    to: `/user-management/${UserType.COMPASS}`,
                },
                {
                    label: 'Lighthouse Users',
                    to: `/user-management/${UserType.LIGHTHOUSE}`,
                },
            ],
        },
        {
            navSection: 'NPI Management',
            navItems: [
                {
                    label: 'Suppliers',
                    to: '/supplier-list',
                },
                {
                    label: 'Prescribers',
                    to: '/prescribers',
                },
            ],
        },
        {
            navSection: 'Route Management',
            navItems: [
                ...(showGlobalRoutingConfig
                    ? [
                          {
                              label: 'Global Routes',
                              to: globalRoutesAppRoute,
                          },
                      ]
                    : []),
                {
                    label: 'Post Live Referral Assignment',
                    to: '/route-management/post-live-referral-assignment',
                },
                {
                    label: 'Route Codes',
                    to: '/route-management/route-codes',
                },
                {
                    label: 'Route Rules',
                    to: '/route-management/route-rules',
                },
                {
                    label: 'Route Lists',
                    to: ListSchemaAppRoutePrefix[ListSchema.assignment],
                    children: ListTypesForSchema[ListSchema.assignment].map((type) => ({
                        label: ListTypeNameMap[type],
                        to: `${ListSchemaAppRoutePrefix[ListSchema.assignment]}/${type}`,
                    })),
                },
            ],
        },
        {
            navSection: 'Program Management',
            navItems: [
                {
                    label: 'Program Rules',
                    to: '/program-management/program-rules',
                },
                {
                    label: 'Program Lists',
                    to: ListSchemaAppRoutePrefix[ListSchema.finance],
                    children: ListTypesForSchema[ListSchema.finance].map((type) => ({
                        label: ListTypeNameMap[type],
                        to: `${ListSchemaAppRoutePrefix[ListSchema.finance]}/${type}`,
                    })),
                },
            ],
        },
        {
            navSection: 'Home Screen Messaging',
            navItems: [
                {
                    label: 'Home Screen Preview',
                    to: '/home-screen-messaging/preview',
                },
                {
                    label: 'Urgent Messages',
                    to: '/home-screen-messaging/urgent-messages',
                },
                {
                    label: 'Announcements',
                    to: '/home-screen-messaging/announcements',
                },
                {
                    label: 'Updates',
                    to: '/home-screen-messaging/updates',
                },
            ],
        },
        {
            navSection: 'Feature Campaign',
            navItems: [
                {
                    label: 'Carousel Announcement',
                    to: '/feature-campaign/carousel',
                },
            ],
        },
    ];

    const onLogOutClick = () => {
        sessionStorage.clear();
        window.pendo.clearSession?.(); // Optional chaining to avoid error if pendo is not initialized
        logout({ logoutParams: { returnTo: `${window.location.origin}/logged-out` } });
    };

    return (
        <div className="sds_main-content">
            {location.pathname.startsWith('/home-screen-messaging/preview/') ? (
                children
            ) : (
                <CollapsibleSidebarAndMainContent
                    sidebarContent={
                        <LeftSideNav
                            navTabContent={navTabContent}
                            loggedInUser={user?.name}
                            onLogOutClick={onLogOutClick}
                            fmhEnvironmentForTag={process.env.REACT_APP_ENV}
                        />
                    }
                    mainContent={children}
                    noSidebarPadding
                />
            )}
        </div>
    );
};

SideNav.propTypes = {
    children: PropTypes.node.isRequired,
};
